import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Grid,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    useTheme,
    useMediaQuery,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    Collapse,
    IconButton,
    Divider,
    Container,
    Stack,
    Fade,
    Zoom,
    Chip,
    Card,
    CardContent,
} from "@mui/material";
import {
    ExpandMore,
    ExpandLess,
    School,
    Assessment,
    TrendingUp,
    People,
    EmojiEvents,
    Visibility,
    VisibilityOff
} from "@mui/icons-material";
import axios from "axios";
import useSubjectsAndUnits from "../../hooks/useSubjectsAndUnits";
import api from "../../axios/axiosInstance";
import { SpinningGradelyLoader, FloatingGradelyLoader } from "../../loadingIcons/LoadingIcons";




const DOK_LEVELS = [
    { level: 1, label: "DOK Level 1: Recall", color: "#f44336" },
    { level: 2, label: "DOK Level 2: Skill/Concept", color: "#ff9800" },
    { level: 3, label: "DOK Level 3: Strategic Thinking", color: "#2196f3" },
    { level: 4, label: "DOK Level 4: Extended Thinking", color: "#4caf50" },
];


const DokDistribution = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [subjectId, setSubjectId] = useState("");
    const [termNumber, setTermNumber] = useState("");
    const [data, setData] = useState([]);
    const [expandedUnits, setExpandedUnits] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const { subjects } = useSubjectsAndUnits();

    const fetchData = async () => {
        if (!subjectId || !termNumber) {
            setError("Please select Subject and Term Number.");
            return;
        }

        setLoading(true);
        setError("");

        try {


            const res = await api.get(
                `${process.env.REACT_APP_BACKEND_URL}/grades/StudentAnalytics/DOKDistribution/`,
                {
                    params: { subject: subjectId, term_number: termNumber },

                }
            );
            setData(res.data || []);


        } catch (err) {
            console.error("Failed to fetch DOK distribution:", err);
            setError("Failed to fetch data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (subjectId && termNumber) fetchData();
    }, [subjectId, termNumber]);

    const getStudentsForLevel = (unit, dokLevel) => {
        return unit.students
            .filter((s) => s.achieved_dok_levels?.includes(dokLevel))
            .map((s) => s.full_name);
    };

    const getUnassessedStudents = (unit) => {
        return unit.students
            .filter((s) => (s.achieved_dok_levels?.length || 0) === 0)
            .map((s) => s.full_name);
    };

    const getTopPerformers = (unit) => {
        const maxLevel = Math.max(...unit.students.map((s) => Math.max(...(s.achieved_dok_levels || [0]))));
        return unit.students
            .filter((s) => (Math.max(...(s.achieved_dok_levels || [0])) === maxLevel))
            .map((s) => s.full_name);
    };

    const toggleExpanded = (unitId) => {
        setExpandedUnits((prev) => ({ ...prev, [unitId]: !prev[unitId] }));
    };

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 } }}>
            <Fade in timeout={800}>
                <Box>
                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    <Paper
                        elevation={3}
                        sx={{
                            p: { xs: 2, sm: 3, md: 4 },
                            mb: 4,
                            borderRadius: { xs: 1.5, sm: 2, md: 3 },
                            background: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`
                        }}
                    >
                        <Typography
                            variant={isSmallMobile ? "h5" : "h4"}
                            fontWeight="bold"
                            gutterBottom
                            sx={{
                                textAlign: 'center',
                                color: theme.palette.primary.main,
                                mb: 3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2
                            }}
                        >
                            <Assessment sx={{ fontSize: 'inherit' }} />
                            DOK Level Distribution Analytics
                        </Typography>

                        <Grid container spacing={3} mb={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Subject</InputLabel>
                                    <Select
                                        value={subjectId}
                                        label="Subject"
                                        onChange={(e) => setSubjectId(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: { xs: 1, sm: 1.5, md: 2 }
                                            }
                                        }}
                                    >
                                        <MenuItem value="">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <School fontSize="small" />
                                                Select Subject
                                            </Box>
                                        </MenuItem>
                                        {subjects.map((s) => (
                                            <MenuItem key={s.id} value={s.id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <School fontSize="small" />
                                                    {s.subject_name} (Class {s.class_level})
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Term Number</InputLabel>
                                    <Select
                                        value={termNumber}
                                        label="Term Number"
                                        onChange={(e) => setTermNumber(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: { xs: 1, sm: 1.5, md: 2 }
                                            }
                                        }}
                                    >
                                        <MenuItem value="">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <TrendingUp fontSize="small" />
                                                Select Term
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="1">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <TrendingUp fontSize="small" />
                                                Term 1
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="2">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <TrendingUp fontSize="small" />
                                                Term 2
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="3">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <TrendingUp fontSize="small" />
                                                Term 3
                                            </Box>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Paper>

                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <CircularProgress size={60} sx={{ color: theme.palette.primary.main }} />
                        </Box>
                    ) : data.length === 0 ? (
                        <Alert severity="info" sx={{ borderRadius: { xs: 1, sm: 1.5, md: 2 } }}>
                            No data found for the selected criteria.
                        </Alert>
                    ) : (
                        <Stack spacing={4}>
                            {data.map((unit, index) => (
                                <Zoom in timeout={600 + index * 100} key={unit.unit_id}>
                                    <Paper
                                        elevation={3}
                                        sx={{
                                            borderRadius: { xs: 1.5, sm: 2, md: 3 },
                                            border: `1px solid ${theme.palette.divider}`,
                                            overflow: 'hidden',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                boxShadow: 6,
                                                transform: 'translateY(-2px)'
                                            }
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{
                                                p: { xs: 2, sm: 3 },
                                                backgroundColor: theme.palette.primary.main,
                                                color: 'white'
                                            }}
                                        >
                                            <Typography
                                                variant={isSmallMobile ? "h6" : "h5"}
                                                fontWeight="bold"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}
                                            >
                                                <People fontSize="small" />
                                                Unit: {unit.unit_name}
                                            </Typography>
                                            <IconButton
                                                onClick={() => toggleExpanded(unit.unit_id)}
                                                sx={{
                                                    color: 'white',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}
                                            >
                                                {expandedUnits[unit.unit_id] ? <ExpandLess /> : <ExpandMore />}
                                            </IconButton>
                                        </Box>

                                        <TableContainer>
                                            <Table>
                                                <TableHead sx={{
                                                    backgroundColor: theme.palette.primary.light,
                                                    '& .MuiTableCell-head': {
                                                        fontWeight: 'bold',
                                                        color: theme.palette.primary.contrastText,
                                                        fontSize: isSmallMobile ? '0.75rem' : '0.875rem'
                                                    }
                                                }}>
                                                    <TableRow>
                                                        {DOK_LEVELS.map((level) => (
                                                            <TableCell key={level.level} align="center">
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                                    <Box
                                                                        sx={{
                                                                            width: 12,
                                                                            height: 12,
                                                                            borderRadius: '50%',
                                                                            backgroundColor: level.color
                                                                        }}
                                                                    />
                                                                    {isSmallMobile ? `DOK ${level.level}` : level.label}
                                                                </Box>
                                                            </TableCell>
                                                        ))}
                                                        <TableCell align="center">
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                                <VisibilityOff fontSize="small" />
                                                                {isSmallMobile ? 'Not Assessed' : 'Not Assessed'}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    <TableRow>
                                                        {DOK_LEVELS.map((level) => {
                                                            const entry = unit.dok_distribution[level.level] || { count: 0, percentage: 0 };
                                                            const names = getStudentsForLevel(unit, level.level);
                                                            return (
                                                                <TableCell key={level.level} align="center">
                                                                    <Tooltip title={names.length ? names.join(", ") : "—"} arrow>
                                                                        <Box>
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    fontWeight: 'bold',
                                                                                    color: level.color,
                                                                                    fontSize: isSmallMobile ? '0.75rem' : '0.875rem'
                                                                                }}
                                                                            >
                                                                                {entry.percentage}%
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    fontSize: isSmallMobile ? '0.7rem' : '0.8rem',
                                                                                    color: theme.palette.text.secondary
                                                                                }}
                                                                            >
                                                                                ({entry.count} students)
                                                                            </Typography>
                                                                        </Box>
                                                                    </Tooltip>
                                                                </TableCell>
                                                            );
                                                        })}
                                                        <TableCell align="center">
                                                            <Tooltip
                                                                title={getUnassessedStudents(unit).join(", ") || "—"}
                                                                arrow
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight="bold"
                                                                    color="error"
                                                                    sx={{ fontSize: isSmallMobile ? '0.75rem' : '0.875rem' }}
                                                                >
                                                                    {unit.not_assessed} students
                                                                </Typography>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        <Collapse in={expandedUnits[unit.unit_id]}>
                                            <Box
                                                sx={{
                                                    p: { xs: 2, sm: 3 },
                                                    backgroundColor: theme.palette.background.default,
                                                    borderTop: `1px solid ${theme.palette.divider}`
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight="bold"
                                                    gutterBottom
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        color: theme.palette.primary.main
                                                    }}
                                                >
                                                    <People fontSize="small" />
                                                    Students by DOK Level
                                                </Typography>

                                                <Grid container spacing={2}>
                                                    {DOK_LEVELS.map((level) => {
                                                        const students = getStudentsForLevel(unit, level.level);
                                                        if (!students.length) return null;
                                                        return (
                                                            <Grid item xs={12} sm={6} key={level.level}>
                                                                <Card
                                                                    elevation={1}
                                                                    sx={{
                                                                        border: `2px solid ${level.color}`,
                                                                        borderRadius: { xs: 1, sm: 1.5 }
                                                                    }}
                                                                >
                                                                    <CardContent sx={{ p: 2 }}>
                                                                        <Typography
                                                                            fontWeight="bold"
                                                                            color="primary"
                                                                            sx={{
                                                                                mb: 1,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: 1
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                sx={{
                                                                                    width: 8,
                                                                                    height: 8,
                                                                                    borderRadius: '50%',
                                                                                    backgroundColor: level.color
                                                                                }}
                                                                            />
                                                                            {level.label}
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                            {students.map((student, idx) => (
                                                                                <Chip
                                                                                    key={idx}
                                                                                    label={student}
                                                                                    size="small"
                                                                                    sx={{
                                                                                        backgroundColor: level.color,
                                                                                        color: 'white',
                                                                                        fontSize: '0.7rem'
                                                                                    }}
                                                                                />
                                                                            ))}
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>
                                                        );
                                                    })}
                                                </Grid>

                                                {getUnassessedStudents(unit).length > 0 && (
                                                    <Box mt={3}>
                                                        <Card
                                                            elevation={1}
                                                            sx={{
                                                                border: '2px solid #f44336',
                                                                borderRadius: { xs: 1, sm: 1.5 }
                                                            }}
                                                        >
                                                            <CardContent sx={{ p: 2 }}>
                                                                <Typography
                                                                    fontWeight="bold"
                                                                    color="error"
                                                                    sx={{
                                                                        mb: 1,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1
                                                                    }}
                                                                >
                                                                    <VisibilityOff fontSize="small" />
                                                                    Not Assessed
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                    {getUnassessedStudents(unit).map((student, idx) => (
                                                                        <Chip
                                                                            key={idx}
                                                                            label={student}
                                                                            size="small"
                                                                            sx={{
                                                                                backgroundColor: '#f44336',
                                                                                color: 'white',
                                                                                fontSize: '0.7rem'
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </CardContent>
                                                        </Card>
                                                    </Box>
                                                )}

                                                <Divider sx={{ my: 3 }} />

                                                <Box
                                                    sx={{
                                                        p: 2,
                                                        backgroundColor: theme.palette.success.light,
                                                        borderRadius: { xs: 1, sm: 1.5 },
                                                        border: `2px solid ${theme.palette.success.main}`
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle2"
                                                        color="success.dark"
                                                        fontWeight="bold"
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1
                                                        }}
                                                    >
                                                        <EmojiEvents fontSize="small" />
                                                        Top Performers: {getTopPerformers(unit).join(", ") || "—"}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Collapse>
                                    </Paper>
                                </Zoom>
                            ))}
                        </Stack>
                    )}
                </Box>
            </Fade>

            {/* Loading State */}
            {loading && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        backdropFilter: "blur(8px)",
                        zIndex: 2000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            borderRadius: 4,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            backgroundColor: "white",
                            maxWidth: 400,
                            mx: 2
                        }}
                    >
                        <FloatingGradelyLoader
                            size={60}
                            showText={true}
                            text="Analyzing DOK level distribution across students..."
                        />
                    </Paper>
                </Box>
            )}
        </Container>
    );
};

export default DokDistribution;
